import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import App from './App.jsx';
import { AuthProvider } from './context/AuthContext.jsx';
import ComingSoon from './pages/ComingSoon.jsx';
import InternalGate from './pages/InternalGate.jsx';
import './i18n';   // registers en/es, must come before any t() call
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// Pre-launch gate. When VITE_COMING_SOON=true (set in .env.prod while the
// backend is still being provisioned), render the launch page standalone —
// no Router, no AuthProvider, no API calls. Flip the flag off and redeploy
// when you're ready to expose the real app.
const isComingSoon = import.meta.env.VITE_COMING_SOON === 'true';

/*
 * Two independent gates, and the order matters.
 *
 * ComingSoon is OUTERMOST. On a pre-launch production the launch page IS the
 * public face of the site, so it must render to anyone — a stranger meeting an
 * OTP prompt on nacisza.com learns more about the system than the launch page
 * tells them, and cannot get past it anyway.
 *
 * InternalGate then guards the real application beneath it. Keeping both flags
 * on is the safe combination: the public sees the launch page today, and the
 * day VITE_COMING_SOON is flipped to false the allow-list is still standing
 * between strangers and the app. Turning the gate off to reveal the launch
 * page would have removed that protection silently.
 *
 * ComingSoon mounts no Router, no AuthProvider and no query client, so the
 * pre-launch build makes no API calls at all.
 */
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    {isComingSoon ? (
      <ComingSoon />
    ) : (
      <InternalGate>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <App />
              <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
            </AuthProvider>
          </QueryClientProvider>
        </BrowserRouter>
      </InternalGate>
    )}
  </React.StrictMode>,
);
